// src/pages/PlatformAdmin.tsx
// Platform-wide admin panel for managing networks, sites, users, and tool access
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, Site, Network } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import {
  Users, LogOut, Plus, Trash2, Eye, EyeOff,
  Building2, Globe, Mail, LayoutGrid, UserPlus,
  Clock, Check, XCircle, Send, RefreshCw, Wrench, CheckCircle,
  Link, ExternalLink, Edit2, Save, X, Gift
} from 'lucide-react';
import SiteSelector from '../components/SiteSelector';
import { ThemeToggle } from '../theme/ThemeToggle';
import './PlatformAdmin.css';

interface SiteAccess {
  site_id: string;
  site_name: string;
  network_id?: string;
  network_name: string;
}

interface NetworkAccess {
  network_id: string;
  network_name: string;
}

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'administrator' | 'basic_user';
  created_at: string;
  deleted_at?: string | null;
  site_access?: SiteAccess[];
  network_access?: NetworkAccess[];
  access_type?: 'site' | 'network'; // For site-specific user lists
}

interface SiteDomain {
  id: string;
  site_id: string;
  domain: string;
  is_primary: boolean;
}

interface Invite {
  id: string;
  email: string;
  site_id: string;
  site?: { name: string; code: string };
  invited_by: string;
  inviter?: { full_name: string; email: string };
  role: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  created_at: string;
}

interface AccessRequest {
  id: string;
  user_id: string | null;
  email: string;
  full_name: string;
  network_id: string | null;
  network?: { name: string };
  requested_site_id: string | null;
  requested_site?: { name: string };
  status: 'pending' | 'approved' | 'denied';
  notes: string | null;
  created_at: string;
}

interface Tool {
  id: string;
  name: string;
  display_name: string;
  description: string;
  icon: string;
  route: string;
  is_active: boolean;
  is_default_enabled: boolean;
  display_order: number;
  network_override?: boolean | null;
  network_disabled?: boolean;
  site_override?: boolean | null;
  site_disabled?: boolean;
  user_override?: boolean | null;
  effective_enabled?: boolean;
  can_configure?: boolean;
}

interface SiteLink {
  id: string;
  site_id: string;
  name: string;
  url: string;
  description: string | null;
  icon_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface Feature {
  feature_key: string;
  parent_key: string | null;
  display_name: string;
  description: string;
  icon: string;
  route: string;
  is_active: boolean;
  is_default_granted: boolean;
  feature_type: 'tool' | 'feature' | 'addon';
  display_order: number;
}

interface Entitlement {
  id: string;
  scope: 'network' | 'site' | 'user';
  network_id: string | null;
  site_id: string | null;
  user_id: string | null;
  feature_key: string;
  is_granted: boolean;
  granted_at: string;
  expires_at: string | null;
  source: 'subscription' | 'trial' | 'manual' | 'promotional' | 'migration';
  source_id: string | null;
  granted_by: string;
  notes: string | null;
  features?: { display_name: string };
}

const PlatformAdmin: React.FC = () => {
  const { user, currentSite, isSuperAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('user-management');
  const [users, setUsers] = useState<User[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'basic_user' as 'super_admin' | 'administrator' | 'basic_user'
  });

  // Multi-site state
  const [networks, setNetworks] = useState<Network[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteDomains, setSiteDomains] = useState<SiteDomain[]>([]);

  // Network management
  const [newNetwork, setNewNetwork] = useState({ name: '', code: '' });

  // Site management
  const [newSite, setNewSite] = useState({
    network_id: '',
    name: '',
    code: '',
    timezone: 'America/New_York',
    branding_name: ''
  });

  // Domain management
  const [newDomain, setNewDomain] = useState('');
  const [selectedSiteForDomains, setSelectedSiteForDomains] = useState<string>('');

  // Global Management state
  const [globalNavLevel, setGlobalNavLevel] = useState<'networks' | 'sites' | 'users'>('networks');
  const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null);
  const [selectedSiteForGlobal, setSelectedSiteForGlobal] = useState<Site | null>(null);
  const [siteUsers, setSiteUsers] = useState<User[]>([]);

  // Editing state for inline editing
  const [editingNetworkId, setEditingNetworkId] = useState<string | null>(null);
  const [editingNetworkData, setEditingNetworkData] = useState({ name: '', code: '' });
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editingSiteData, setEditingSiteData] = useState({ name: '', code: '', timezone: '', branding_name: '' });

  // User management - Invites & Access Requests
  const [invites, setInvites] = useState<Invite[]>([]);
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [newInvite, setNewInvite] = useState({ email: '', site_id: '', role: 'basic_user' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState<string | null>(null);

  // Tool management state
  const [tools, setTools] = useState<Tool[]>([]);
  const [selectedNetworkForTools, setSelectedNetworkForTools] = useState<string>('');
  const [selectedSiteForTools, setSelectedSiteForTools] = useState<string>('');
  const [selectedUserForTools, setSelectedUserForTools] = useState<string>('');
  const [networkTools, setNetworkTools] = useState<Tool[]>([]);
  const [siteToolsData, setSiteToolsData] = useState<Tool[]>([]);
  const [userToolsData, setUserToolsData] = useState<Tool[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  // Site links (Quick Links) state
  const [siteLinks, setSiteLinks] = useState<SiteLink[]>([]);
  const [siteLinksLoading, setSiteLinksLoading] = useState(false);
  const [selectedSiteForLinks, setSelectedSiteForLinks] = useState<string>('');
  const [newLink, setNewLink] = useState({ name: '', url: '', description: '' });
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [editingLinkData, setEditingLinkData] = useState({ name: '', url: '', description: '' });

  // Entitlements state
  const [features, setFeatures] = useState<Feature[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [selectedNetworkForEntitlements, setSelectedNetworkForEntitlements] = useState<string>('');
  const [selectedSiteForEntitlements, setSelectedSiteForEntitlements] = useState<string>('');
  const [selectedUserForEntitlements, setSelectedUserForEntitlements] = useState<string>('');
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialForm, setTrialForm] = useState({ site_id: '', feature_key: '', duration_days: 30, notes: '' });

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin) {
      fetchNetworks();
      fetchAllSites();
    }
  }, [isSuperAdmin]);

  // Fetch site domains when viewing domains tab
  useEffect(() => {
    const siteId = selectedSiteForDomains || currentSite?.id;
    if (activeTab === 'domains' && siteId) {
      fetchSiteDomains(siteId);
    }
  }, [activeTab, selectedSiteForDomains, currentSite?.id]);

  // Fetch invites and access requests when viewing user-management tab
  useEffect(() => {
    if (activeTab === 'user-management') {
      fetchInvites();
      fetchAccessRequests();
    }
  }, [activeTab, currentSite?.id]);

  // Fetch tools when viewing tool-management tab
  useEffect(() => {
    if (activeTab === 'tool-management') {
      fetchTools();
    }
  }, [activeTab]);

  // Fetch network tools when network is selected
  useEffect(() => {
    if (selectedNetworkForTools) {
      fetchNetworkTools(selectedNetworkForTools);
    }
  }, [selectedNetworkForTools]);

  // Fetch site tools when site is selected
  useEffect(() => {
    if (selectedSiteForTools) {
      fetchSiteTools(selectedSiteForTools);
    }
  }, [selectedSiteForTools]);

  // Fetch user tools when user is selected
  useEffect(() => {
    if (selectedUserForTools && selectedSiteForTools) {
      fetchUserTools(selectedSiteForTools, selectedUserForTools);
    }
  }, [selectedUserForTools, selectedSiteForTools]);

  // Fetch site links when site is selected for links tab
  useEffect(() => {
    if (activeTab === 'quicklinks' && selectedSiteForLinks) {
      fetchSiteLinks(selectedSiteForLinks);
    }
  }, [activeTab, selectedSiteForLinks]);

  // Fetch features when tool-management tab is active
  useEffect(() => {
    if (activeTab === 'tool-management') {
      fetchFeatures();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNetworks = async () => {
    try {
      const response = await api.get('/api/networks');
      setNetworks(response.data);
    } catch (error) {
      console.error('Error fetching networks:', error);
    }
  };

  const fetchAllSites = async () => {
    try {
      const response = await api.get('/api/sites');
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchSiteDomains = async (siteId?: string) => {
    const targetSiteId = siteId || selectedSiteForDomains || currentSite?.id;
    if (!targetSiteId) return;
    try {
      const response = await api.get(`/api/sites/${targetSiteId}/domains`);
      setSiteDomains(response.data);
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'super_admin' | 'administrator' | 'basic_user') => {
    try {
      await api.patch(`/api/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Error updating user role');
    }
  };

  const deleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action can be undone by restoring the user.`)) {
      return;
    }

    try {
      await api.delete(`/api/users/${userId}`);
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.error || 'Error deleting user');
    }
  };

  const restoreUser = async (userId: string) => {
    try {
      await api.post(`/api/users/${userId}/restore`);
      fetchUsers();
    } catch (error: any) {
      console.error('Error restoring user:', error);
      alert(error.response?.data?.error || 'Error restoring user');
    }
  };

  // Network management functions
  const addNetwork = async () => {
    if (!newNetwork.name.trim() || !newNetwork.code.trim()) return;

    try {
      await api.post('/api/networks', newNetwork);
      setNewNetwork({ name: '', code: '' });
      fetchNetworks();
    } catch (error) {
      console.error('Error adding network:', error);
      alert('Error adding network');
    }
  };

  const updateNetwork = async (networkId: string, data: Partial<Network>) => {
    try {
      await api.patch(`/api/networks/${networkId}`, data);
      fetchNetworks();
    } catch (error) {
      console.error('Error updating network:', error);
      alert('Error updating network');
    }
  };

  // Global Management navigation functions
  const navigateToNetwork = (network: Network) => {
    setSelectedNetwork(network);
    setSelectedSiteForGlobal(null);
    setGlobalNavLevel('sites');
  };

  const navigateToSite = (site: Site) => {
    setSelectedSiteForGlobal(site);
    setGlobalNavLevel('users');
    fetchSiteUsers(site.id);
  };

  const navigateBackToNetworks = () => {
    setSelectedNetwork(null);
    setSelectedSiteForGlobal(null);
    setGlobalNavLevel('networks');
    setSiteUsers([]);
  };

  const navigateBackToSites = () => {
    setSelectedSiteForGlobal(null);
    setGlobalNavLevel('sites');
    setSiteUsers([]);
  };

  const fetchSiteUsers = async (siteId: string) => {
    try {
      const response = await api.get(`/api/sites/${siteId}/users`);
      setSiteUsers(response.data);
    } catch (error) {
      console.error('Error fetching site users:', error);
    }
  };

  // Network inline editing functions
  const startEditingNetwork = (network: Network) => {
    setEditingNetworkId(network.id);
    setEditingNetworkData({ name: network.name, code: network.code });
  };

  const saveNetworkEdit = async () => {
    if (!editingNetworkId) return;
    await updateNetwork(editingNetworkId, editingNetworkData);
    setEditingNetworkId(null);
  };

  const cancelNetworkEdit = () => {
    setEditingNetworkId(null);
  };

  // Site inline editing functions
  const startEditingSite = (site: Site) => {
    setEditingSiteId(site.id);
    setEditingSiteData({
      name: site.name,
      code: site.code,
      timezone: site.timezone || 'America/New_York',
      branding_name: site.branding_name || ''
    });
  };

  const saveSiteEdit = async () => {
    if (!editingSiteId) return;
    await updateSite(editingSiteId, editingSiteData);
    setEditingSiteId(null);
  };

  const cancelSiteEdit = () => {
    setEditingSiteId(null);
  };

  // Get sites for selected network
  const getNetworkSites = () => {
    if (!selectedNetwork) return [];
    return sites.filter(site => site.network_id === selectedNetwork.id);
  };

  // Get counts for networks
  const getNetworkSiteCount = (networkId: string) => sites.filter(s => s.network_id === networkId).length;

  // Site management functions
  const addSite = async () => {
    if (!newSite.name.trim() || !newSite.code.trim() || !newSite.network_id) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/api/sites', newSite);
      setNewSite({ network_id: '', name: '', code: '', timezone: 'America/New_York', branding_name: '' });
      fetchAllSites();
    } catch (error) {
      console.error('Error adding site:', error);
      alert('Error adding site');
    }
  };

  const updateSite = async (siteId: string, data: Partial<Site>) => {
    try {
      await api.patch(`/api/sites/${siteId}`, data);
      fetchAllSites();
    } catch (error) {
      console.error('Error updating site:', error);
      alert('Error updating site');
    }
  };

  // Domain management functions
  const getSelectedSiteForDomains = () => selectedSiteForDomains || currentSite?.id;

  const addDomain = async () => {
    const targetSiteId = getSelectedSiteForDomains();
    if (!newDomain.trim() || !targetSiteId) return;

    try {
      await api.post(`/api/sites/${targetSiteId}/domains`, {
        domain: newDomain.toLowerCase(),
        is_primary: siteDomains.length === 0
      });
      setNewDomain('');
      fetchSiteDomains(targetSiteId);
    } catch (error) {
      console.error('Error adding domain:', error);
      alert('Error adding domain');
    }
  };

  const deleteDomain = async (domainId: string) => {
    const targetSiteId = getSelectedSiteForDomains();
    if (!targetSiteId || !window.confirm('Are you sure you want to delete this domain?')) return;

    try {
      await api.delete(`/api/sites/${targetSiteId}/domains/${domainId}`);
      fetchSiteDomains(targetSiteId);
    } catch (error) {
      console.error('Error deleting domain:', error);
      alert('Error deleting domain');
    }
  };

  // Invite management functions
  const fetchInvites = async () => {
    try {
      const response = await api.get('/api/invites');
      setInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchAccessRequests = async () => {
    try {
      const response = await api.get('/api/access-requests');
      setAccessRequests(response.data);
    } catch (error) {
      console.error('Error fetching access requests:', error);
    }
  };

  const sendInvite = async () => {
    const siteId = newInvite.site_id || currentSite?.id;
    if (!newInvite.email || !siteId) {
      alert('Please enter an email and select a site');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await api.post('/api/invites', {
        email: newInvite.email,
        site_id: siteId,
        role: newInvite.role
      });
      setNewInvite({ email: '', site_id: currentSite?.id || '', role: 'basic_user' });
      fetchInvites();

      // Check if email was sent successfully
      if (response.data.emailSent) {
        alert('Invite sent successfully!');
      } else {
        const errorMsg = response.data.emailError || 'Unknown error';
        alert(`Invite created but email failed to send: ${errorMsg}\n\nThe user can still use the invite link manually.`);
      }
    } catch (error: any) {
      console.error('Error sending invite:', error);
      alert(error.response?.data?.error || 'Error sending invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const revokeInvite = async (inviteId: string) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;

    try {
      await api.delete(`/api/invites/${inviteId}`);
      fetchInvites();
    } catch (error) {
      console.error('Error revoking invite:', error);
      alert('Error revoking invite');
    }
  };

  const resendInvite = async (inviteId: string) => {
    try {
      const response = await api.post(`/api/invites/${inviteId}/resend`);

      // Check if email was sent successfully
      if (response.data.emailSent) {
        alert('Invite resent successfully!');
      } else {
        const errorMsg = response.data.emailError || 'Unknown error';
        alert(`Invite extended but email failed to send: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error resending invite:', error);
      alert('Error resending invite');
    }
  };

  const approveAccessRequest = async (requestId: string, siteId: string) => {
    setRequestActionLoading(requestId);
    try {
      await api.patch(`/api/access-requests/${requestId}`, {
        status: 'approved',
        site_id: siteId
      });
      fetchAccessRequests();
      fetchUsers();
    } catch (error: any) {
      console.error('Error approving request:', error);
      alert(error.response?.data?.error || 'Error approving request');
    } finally {
      setRequestActionLoading(null);
    }
  };

  const denyAccessRequest = async (requestId: string) => {
    const notes = window.prompt('Optional: Enter a reason for denial');

    setRequestActionLoading(requestId);
    try {
      await api.patch(`/api/access-requests/${requestId}`, {
        status: 'denied',
        notes
      });
      fetchAccessRequests();
    } catch (error: any) {
      console.error('Error denying request:', error);
      alert(error.response?.data?.error || 'Error denying request');
    } finally {
      setRequestActionLoading(null);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Tool management functions
  const fetchTools = async () => {
    setToolsLoading(true);
    try {
      const response = await api.get('/api/tools');
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setToolsLoading(false);
    }
  };

  const fetchNetworkTools = async (networkId: string) => {
    setToolsLoading(true);
    try {
      const response = await api.get(`/api/networks/${networkId}/tools`);
      setNetworkTools(response.data);
    } catch (error) {
      console.error('Error fetching network tools:', error);
    } finally {
      setToolsLoading(false);
    }
  };

  const fetchSiteTools = async (siteId: string) => {
    setToolsLoading(true);
    try {
      const response = await api.get(`/api/sites/${siteId}/tools`);
      setSiteToolsData(response.data);
    } catch (error) {
      console.error('Error fetching site tools:', error);
    } finally {
      setToolsLoading(false);
    }
  };

  const fetchUserTools = async (siteId: string, userId: string) => {
    setToolsLoading(true);
    try {
      const response = await api.get(`/api/sites/${siteId}/users/${userId}/tools`);
      setUserToolsData(response.data);
    } catch (error) {
      console.error('Error fetching user tools:', error);
    } finally {
      setToolsLoading(false);
    }
  };

  const toggleGlobalToolActive = async (toolId: string, isActive: boolean) => {
    try {
      await api.patch(`/api/tools/${toolId}`, { is_active: isActive });
      fetchTools();
    } catch (error) {
      console.error('Error toggling tool:', error);
      alert('Error updating tool');
    }
  };

  const toggleNetworkTool = async (networkId: string, toolId: string, enable: boolean | null) => {
    try {
      if (enable === null) {
        await api.delete(`/api/networks/${networkId}/tools/${toolId}`);
      } else {
        await api.put(`/api/networks/${networkId}/tools/${toolId}`, { is_enabled: enable });
      }
      fetchNetworkTools(networkId);
    } catch (error) {
      console.error('Error toggling network tool:', error);
      alert('Error updating network tool access');
    }
  };

  const toggleSiteTool = async (siteId: string, toolId: string, enable: boolean | null) => {
    try {
      if (enable === null) {
        await api.delete(`/api/sites/${siteId}/tools/${toolId}`);
      } else {
        await api.put(`/api/sites/${siteId}/tools/${toolId}`, { is_enabled: enable });
      }
      fetchSiteTools(siteId);
    } catch (error) {
      console.error('Error toggling site tool:', error);
      alert('Error updating site tool access');
    }
  };

  const toggleUserTool = async (siteId: string, userId: string, toolId: string, enable: boolean | null) => {
    try {
      if (enable === null) {
        await api.delete(`/api/sites/${siteId}/users/${userId}/tools/${toolId}`);
      } else {
        await api.put(`/api/sites/${siteId}/users/${userId}/tools/${toolId}`, { is_enabled: enable });
      }
      fetchUserTools(siteId, userId);
    } catch (error) {
      console.error('Error toggling user tool:', error);
      alert('Error updating user tool access');
    }
  };

  // Site links (Quick Links) functions
  const fetchSiteLinks = async (siteId: string) => {
    setSiteLinksLoading(true);
    try {
      const response = await api.get(`/api/sites/${siteId}/links/all`);
      setSiteLinks(response.data);
    } catch (error) {
      console.error('Error fetching site links:', error);
    } finally {
      setSiteLinksLoading(false);
    }
  };

  const addSiteLink = async () => {
    if (!selectedSiteForLinks || !newLink.name.trim() || !newLink.url.trim()) {
      alert('Please select a site and fill in name and URL');
      return;
    }

    try {
      await api.post(`/api/sites/${selectedSiteForLinks}/links`, {
        name: newLink.name,
        url: newLink.url,
        description: newLink.description || null,
        display_order: siteLinks.length
      });
      setNewLink({ name: '', url: '', description: '' });
      fetchSiteLinks(selectedSiteForLinks);
    } catch (error) {
      console.error('Error adding site link:', error);
      alert('Error adding link');
    }
  };

  const updateSiteLink = async (linkId: string) => {
    if (!selectedSiteForLinks || !editingLinkData.name.trim() || !editingLinkData.url.trim()) {
      alert('Name and URL are required');
      return;
    }

    try {
      await api.patch(`/api/sites/${selectedSiteForLinks}/links/${linkId}`, {
        name: editingLinkData.name,
        url: editingLinkData.url,
        description: editingLinkData.description || null
      });
      setEditingLink(null);
      setEditingLinkData({ name: '', url: '', description: '' });
      fetchSiteLinks(selectedSiteForLinks);
    } catch (error) {
      console.error('Error updating site link:', error);
      alert('Error updating link');
    }
  };

  const deleteSiteLink = async (linkId: string) => {
    if (!selectedSiteForLinks || !window.confirm('Are you sure you want to delete this link?')) return;

    try {
      await api.delete(`/api/sites/${selectedSiteForLinks}/links/${linkId}`);
      fetchSiteLinks(selectedSiteForLinks);
    } catch (error) {
      console.error('Error deleting site link:', error);
      alert('Error deleting link');
    }
  };

  const toggleSiteLinkActive = async (linkId: string, isActive: boolean) => {
    if (!selectedSiteForLinks) return;

    try {
      await api.patch(`/api/sites/${selectedSiteForLinks}/links/${linkId}`, { is_active: isActive });
      fetchSiteLinks(selectedSiteForLinks);
    } catch (error) {
      console.error('Error toggling site link:', error);
      alert('Error updating link');
    }
  };

  // Entitlements functions
  const fetchFeatures = async () => {
    try {
      const response = await api.get('/api/features');
      setFeatures(response.data);
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const fetchEntitlements = async (params: { scope?: string; network_id?: string; site_id?: string; user_id?: string }) => {
    setEntitlementsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.scope) queryParams.append('scope', params.scope);
      if (params.network_id) queryParams.append('network_id', params.network_id);
      if (params.site_id) queryParams.append('site_id', params.site_id);
      if (params.user_id) queryParams.append('user_id', params.user_id);

      const response = await api.get(`/api/entitlements?${queryParams.toString()}`);
      setEntitlements(response.data);
    } catch (error) {
      console.error('Error fetching entitlements:', error);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const toggleFeatureActive = async (featureKey: string, isActive: boolean) => {
    try {
      await api.patch(`/api/features/${featureKey}`, { is_active: isActive });
      fetchFeatures();
    } catch (error) {
      console.error('Error toggling feature:', error);
      alert('Error updating feature');
    }
  };

  const deleteEntitlement = async (entitlementId: string) => {
    try {
      await api.delete(`/api/entitlements/${entitlementId}`);

      // Refresh entitlements based on current selection
      if (selectedNetworkForEntitlements) {
        fetchEntitlements({ scope: 'network', network_id: selectedNetworkForEntitlements });
      } else if (selectedSiteForEntitlements && selectedUserForEntitlements) {
        fetchEntitlements({ scope: 'user', user_id: selectedUserForEntitlements, site_id: selectedSiteForEntitlements });
      } else if (selectedSiteForEntitlements) {
        fetchEntitlements({ scope: 'site', site_id: selectedSiteForEntitlements });
      }
    } catch (error) {
      console.error('Error deleting entitlement:', error);
      alert('Error revoking entitlement');
    }
  };

  const grantEntitlement = async (scope: 'network' | 'site' | 'user', targetId: string, featureKey: string) => {
    try {
      await api.post('/api/entitlements', {
        scope,
        network_id: scope === 'network' ? targetId : null,
        site_id: scope === 'site' ? targetId : null,
        user_id: scope === 'user' ? targetId : null,
        user_site_id: scope === 'user' ? selectedSiteForEntitlements : null,
        feature_key: featureKey,
        source: 'manual'
      });

      // Refresh entitlements based on scope
      if (scope === 'network') {
        fetchEntitlements({ scope: 'network', network_id: targetId });
      } else if (scope === 'site') {
        fetchEntitlements({ scope: 'site', site_id: targetId });
      } else if (scope === 'user') {
        fetchEntitlements({ scope: 'user', user_id: targetId, site_id: selectedSiteForEntitlements });
      }
    } catch (error: any) {
      console.error('Error granting entitlement:', error);
      alert(error.response?.data?.error || 'Error granting entitlement');
    }
  };

  const startTrial = async () => {
    if (!trialForm.site_id || !trialForm.feature_key) {
      alert('Please select a site and feature');
      return;
    }

    try {
      await api.post('/api/trials', {
        site_id: trialForm.site_id,
        feature_key: trialForm.feature_key,
        duration_days: trialForm.duration_days,
        notes: trialForm.notes || null
      });

      setShowTrialModal(false);
      setTrialForm({ site_id: '', feature_key: '', duration_days: 30, notes: '' });
      alert('Trial started successfully!');

      // Refresh if viewing that site's entitlements
      if (selectedSiteForEntitlements === trialForm.site_id) {
        fetchEntitlements({ scope: 'site', site_id: selectedSiteForEntitlements });
      }
    } catch (error: any) {
      console.error('Error starting trial:', error);
      alert(error.response?.data?.error || 'Error starting trial');
    }
  };

  const formatExpirationDate = (date: string | null) => {
    if (!date) return 'Never';
    const expDate = new Date(date);
    const now = new Date();
    const daysLeft = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return 'Expired';
    if (daysLeft === 0) return 'Today';
    if (daysLeft === 1) return '1 day left';
    return `${daysLeft} days left`;
  };

  const getSourceBadgeClass = (source: string) => {
    switch (source) {
      case 'trial': return 'badge-trial';
      case 'promotional': return 'badge-promo';
      case 'subscription': return 'badge-subscription';
      case 'migration': return 'badge-migration';
      default: return 'badge-manual';
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            full_name: newUser.full_name,
            role: newUser.role
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        // Create the profile row via the platform API (data decoupled from
        // supabase in WP-3.4); the auth.signUp above stays on Supabase Auth.
        await api.post('/api/users/profile', {
          id: data.user.id,
          email: newUser.email,
          full_name: newUser.full_name,
          role: newUser.role,
        });

        // Grant site access to the new user for the current site
        if (currentSite) {
          await api.post(`/api/users/${data.user.id}/sites`, {
            site_id: currentSite.id
          });
        }

        alert('User created successfully!');
        setShowAddUserModal(false);
        setNewUser({ email: '', password: '', full_name: '', role: 'basic_user' });
        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + error.message);
    }
  };

  return (
    <div className="platform-admin">
      <div className="admin-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
        <h2>Platform Admin</h2>
        <div className="sidebar-site-selector">
          <SiteSelector />
        </div>
        <nav className="admin-nav">
          <button
            className={activeTab === 'user-management' ? 'active' : ''}
            onClick={() => setActiveTab('user-management')}
          >
            <UserPlus size={16} />
            Invites & Requests
          </button>
          <button
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} />
            Manage Users
          </button>

          {/* Super Admin Only */}
          {isSuperAdmin && (
            <>
              <div className="nav-divider">Super Admin</div>
              <button
                className={activeTab === 'global-management' ? 'active' : ''}
                onClick={() => {
                  setActiveTab('global-management');
                  navigateBackToNetworks();
                }}
              >
                <Globe size={16} />
                Global Management
              </button>
              <button
                className={activeTab === 'domains' ? 'active' : ''}
                onClick={() => setActiveTab('domains')}
              >
                <Mail size={16} />
                Email Domains
              </button>
              <button
                className={activeTab === 'tool-management' ? 'active' : ''}
                onClick={() => setActiveTab('tool-management')}
              >
                <Wrench size={16} />
                Tool Management
              </button>
              <button
                className={activeTab === 'quicklinks' ? 'active' : ''}
                onClick={() => setActiveTab('quicklinks')}
              >
                <Link size={16} />
                Quick Links
              </button>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <button onClick={() => navigate('/')} className="nav-button">
            <LayoutGrid size={16} />
            Tools
          </button>
          <button onClick={handleSignOut} className="nav-button">
            <LogOut size={16} />
            Sign Out
          </button>
          <ThemeToggle variant="neutral" className="sidebar-theme-toggle" />
        </div>
      </div>

      <div className="admin-content">
        {/* User Management Tab - Invites & Access Requests */}
        {activeTab === 'user-management' && (
          <div className="tab-content">
            <h3>User Management</h3>

            {/* Send Invite Section */}
            <div className="management-section">
              <h4><Send size={16} /> Send Invite</h4>
              <p className="settings-description">
                Invite a user to join a site. They will receive an email with a link to create their account.
              </p>
              <div className="invite-form">
                <input
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  placeholder="Email address..."
                />
                <select
                  value={newInvite.site_id || currentSite?.id || ''}
                  onChange={(e) => setNewInvite({ ...newInvite, site_id: e.target.value })}
                >
                  <option value="">Select Site...</option>
                  {(isSuperAdmin ? sites : [currentSite]).filter(Boolean).map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <select
                  value={newInvite.role}
                  onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })}
                >
                  <option value="basic_user">Basic User</option>
                  <option value="administrator">Administrator</option>
                </select>
                <button onClick={sendInvite} className="add-button" disabled={inviteLoading}>
                  <Send size={16} />
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>

            {/* Pending Access Requests */}
            <div className="management-section">
              <h4><Clock size={16} /> Pending Access Requests</h4>
              {accessRequests.filter(r => r.status === 'pending').length === 0 ? (
                <p className="empty-message">No pending access requests.</p>
              ) : (
                <div className="requests-list">
                  {accessRequests.filter(r => r.status === 'pending').map(request => (
                    <div key={request.id} className="request-card">
                      <div className="request-info">
                        <div className="request-name">{request.full_name}</div>
                        <div className="request-email">{request.email}</div>
                        {request.requested_site && (
                          <div className="request-site">Requested: {request.requested_site.name}</div>
                        )}
                        {request.network && (
                          <div className="request-network">Network: {request.network.name}</div>
                        )}
                        <div className="request-date">
                          Submitted: {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="request-actions">
                        <select
                          className="site-select"
                          defaultValue={
                            request.requested_site_id ||
                            (request.network_id ? sites.find(s => s.network_id === request.network_id)?.id : '') ||
                            currentSite?.id ||
                            ''
                          }
                        >
                          {(isSuperAdmin
                            ? (request.network_id
                                ? sites.filter(s => s.network_id === request.network_id)
                                : sites)
                            : [currentSite]
                          ).filter(Boolean).map((s: any) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={(e) => {
                            const select = e.currentTarget.parentElement?.querySelector('.site-select') as HTMLSelectElement;
                            approveAccessRequest(request.id, select?.value || currentSite?.id || '');
                          }}
                          className="btn-approve"
                          disabled={requestActionLoading === request.id}
                        >
                          <Check size={14} />
                          Approve
                        </button>
                        <button
                          onClick={() => denyAccessRequest(request.id)}
                          className="btn-deny"
                          disabled={requestActionLoading === request.id}
                        >
                          <XCircle size={14} />
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invites */}
            <div className="management-section">
              <h4><Mail size={16} /> Pending Invites</h4>
              {invites.filter(i => i.status === 'pending').length === 0 ? (
                <p className="empty-message">No pending invites.</p>
              ) : (
                <div className="invites-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Site</th>
                        <th>Role</th>
                        <th>Expires</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.filter(i => i.status === 'pending').map(invite => (
                        <tr key={invite.id}>
                          <td>{invite.email}</td>
                          <td>{invite.site?.name || '-'}</td>
                          <td>
                            <span className={`role-badge ${invite.role}`}>
                              {invite.role === 'administrator' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td>{new Date(invite.expires_at).toLocaleDateString()}</td>
                          <td>
                            <div className="invite-actions">
                              <button
                                onClick={() => resendInvite(invite.id)}
                                className="icon-button"
                                title="Resend invite"
                              >
                                <RefreshCw size={14} />
                              </button>
                              <button
                                onClick={() => revokeInvite(invite.id)}
                                className="icon-button danger"
                                title="Revoke invite"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="management-section">
              <h4><CheckCircle size={16} /> Recent Activity</h4>
              <div className="activity-list">
                {[...invites.filter(i => i.status !== 'pending').slice(0, 5),
                  ...accessRequests.filter(r => r.status !== 'pending').slice(0, 5)]
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .slice(0, 10)
                  .map((item, index) => (
                    <div key={index} className="activity-item">
                      {'token' in item ? (
                        <>
                          <span className={`status-indicator ${item.status}`}></span>
                          <span>Invite to <strong>{item.email}</strong> - {item.status}</span>
                        </>
                      ) : (
                        <>
                          <span className={`status-indicator ${item.status}`}></span>
                          <span>Request from <strong>{item.full_name}</strong> - {item.status}</span>
                        </>
                      )}
                    </div>
                  ))}
                {invites.filter(i => i.status !== 'pending').length === 0 &&
                 accessRequests.filter(r => r.status !== 'pending').length === 0 && (
                  <p className="empty-message">No recent activity.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3>Manage Users</h3>
              <button onClick={() => setShowAddUserModal(true)} className="add-button">
                <Plus size={16} />
                Add User
              </button>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    {isSuperAdmin && <th>Network / Site Access</th>}
                    <th>Role</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.full_name}</td>
                      <td>{u.email}</td>
                      {isSuperAdmin && (
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>
                            {/* Network Access */}
                            {u.network_access && u.network_access.length > 0 && (
                              <div style={{ marginBottom: '0.25rem' }}>
                                {u.network_access.map((na, idx) => (
                                  <span
                                    key={idx}
                                    className="status-badge active"
                                    style={{ marginRight: '0.25rem', marginBottom: '0.25rem', display: 'inline-block' }}
                                  >
                                    {na.network_name} (Network)
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Site Access */}
                            {u.site_access && u.site_access.length > 0 && (
                              <div>
                                {u.site_access.map((sa, idx) => (
                                  <span
                                    key={idx}
                                    className="status-badge info"
                                    style={{ marginRight: '0.25rem', marginBottom: '0.25rem', display: 'inline-block' }}
                                  >
                                    {sa.site_name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* No access */}
                            {(!u.network_access || u.network_access.length === 0) &&
                             (!u.site_access || u.site_access.length === 0) && (
                              <span style={{ color: '#9aa0a6', fontStyle: 'italic' }}>No access assigned</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td>
                        <span className={`role-badge ${u.role}`}>
                          {u.role === 'super_admin' ? 'Super Admin' : u.role === 'administrator' ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {u.id !== user?.id && isSuperAdmin && (
                            <>
                              <select
                                value={u.role}
                                onChange={(e) => updateUserRole(u.id, e.target.value as any)}
                                className="role-select"
                              >
                                <option value="basic_user">Basic User</option>
                                <option value="administrator">Administrator</option>
                                <option value="super_admin">Super Admin</option>
                              </select>
                              <button
                                onClick={() => deleteUser(u.id, u.full_name || u.email)}
                                className="icon-button danger"
                                title="Delete user"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add User Modal */}
            {showAddUserModal && (
              <div className="modal-overlay" onClick={() => setShowAddUserModal(false)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <h2>Add New User</h2>
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Enter password (min 6 characters)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
                      className="role-select"
                      style={{ width: '100%', padding: '0.5rem' }}
                    >
                      <option value="basic_user">Basic User</option>
                      <option value="administrator">Administrator</option>
                      {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button onClick={() => setShowAddUserModal(false)} className="btn-secondary">
                      Cancel
                    </button>
                    <button onClick={createUser} className="btn-primary">
                      Create User
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Global Management Tab (Super Admin) */}
        {activeTab === 'global-management' && isSuperAdmin && (
          <div className="tab-content">
            <h3>Global Management</h3>

            {/* Breadcrumb Navigation */}
            <div className="breadcrumb" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={navigateBackToNetworks}
                className={`breadcrumb-item ${globalNavLevel === 'networks' ? 'active' : ''}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: globalNavLevel === 'networks' ? '#1a73e8' : '#5f6368',
                  fontWeight: globalNavLevel === 'networks' ? 600 : 400,
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px'
                }}
              >
                Networks
              </button>
              {selectedNetwork && (
                <>
                  <span style={{ color: '#9aa0a6' }}>/</span>
                  <button
                    onClick={navigateBackToSites}
                    className={`breadcrumb-item ${globalNavLevel === 'sites' ? 'active' : ''}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: globalNavLevel === 'sites' ? '#1a73e8' : '#5f6368',
                      fontWeight: globalNavLevel === 'sites' ? 600 : 400,
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px'
                    }}
                  >
                    {selectedNetwork.name}
                  </button>
                </>
              )}
              {selectedSiteForGlobal && (
                <>
                  <span style={{ color: '#9aa0a6' }}>/</span>
                  <span
                    style={{
                      color: '#1a73e8',
                      fontWeight: 600,
                      padding: '0.25rem 0.5rem'
                    }}
                  >
                    {selectedSiteForGlobal.name}
                  </span>
                </>
              )}
            </div>

            {/* Networks Level */}
            {globalNavLevel === 'networks' && (
              <>
                <div className="add-form" style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={newNetwork.name}
                    onChange={(e) => setNewNetwork({ ...newNetwork, name: e.target.value })}
                    placeholder="Network name..."
                  />
                  <input
                    type="text"
                    value={newNetwork.code}
                    onChange={(e) => setNewNetwork({ ...newNetwork, code: e.target.value.toUpperCase() })}
                    placeholder="Code (e.g., WCR)..."
                    style={{ width: '120px' }}
                  />
                  <button onClick={addNetwork} className="add-button">
                    <Plus size={16} />
                    Add Network
                  </button>
                </div>

                <div className="sites-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Network Name</th>
                        <th>Code</th>
                        <th>Sites</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {networks.map(network => (
                        <tr key={network.id} className={!network.is_active ? 'inactive' : ''}>
                          {editingNetworkId === network.id ? (
                            <>
                              <td>
                                <input
                                  type="text"
                                  value={editingNetworkData.name}
                                  onChange={(e) => setEditingNetworkData({ ...editingNetworkData, name: e.target.value })}
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingNetworkData.code}
                                  onChange={(e) => setEditingNetworkData({ ...editingNetworkData, code: e.target.value.toUpperCase() })}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>{getNetworkSiteCount(network.id)}</td>
                              <td>
                                <div className="room-actions">
                                  <button onClick={saveNetworkEdit} className="icon-button" title="Save">
                                    <Save size={16} />
                                  </button>
                                  <button onClick={cancelNetworkEdit} className="icon-button" title="Cancel">
                                    <X size={16} />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{network.name}</td>
                              <td><code>{network.code}</code></td>
                              <td>{getNetworkSiteCount(network.id)}</td>
                              <td>
                                <div className="room-actions">
                                  <button onClick={() => startEditingNetwork(network)} className="icon-button" title="Edit">
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => navigateToNetwork(network)}
                                    className="icon-button"
                                    title="View Sites"
                                    style={{ color: '#1a73e8' }}
                                  >
                                    <Building2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Sites Level */}
            {globalNavLevel === 'sites' && selectedNetwork && (
              <>
                <div className="add-form site-form" style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={newSite.name}
                    onChange={(e) => setNewSite({ ...newSite, name: e.target.value, network_id: selectedNetwork.id })}
                    placeholder="Site name..."
                  />
                  <input
                    type="text"
                    value={newSite.code}
                    onChange={(e) => setNewSite({ ...newSite, code: e.target.value.toUpperCase(), network_id: selectedNetwork.id })}
                    placeholder="Code..."
                    style={{ width: '100px' }}
                  />
                  <select
                    value={newSite.timezone}
                    onChange={(e) => setNewSite({ ...newSite, timezone: e.target.value, network_id: selectedNetwork.id })}
                  >
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Phoenix">Arizona (MST)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="America/Anchorage">Alaska (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii (HST)</option>
                  </select>
                  <button
                    onClick={() => {
                      setNewSite({ ...newSite, network_id: selectedNetwork.id });
                      addSite();
                    }}
                    className="add-button"
                  >
                    <Plus size={16} />
                    Add Site
                  </button>
                </div>

                <div className="sites-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Site Name</th>
                        <th>Code</th>
                        <th>Timezone</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getNetworkSites().map(site => (
                        <tr key={site.id} className={!site.is_active ? 'inactive' : ''}>
                          {editingSiteId === site.id ? (
                            <>
                              <td>
                                <input
                                  type="text"
                                  value={editingSiteData.name}
                                  onChange={(e) => setEditingSiteData({ ...editingSiteData, name: e.target.value })}
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingSiteData.code}
                                  onChange={(e) => setEditingSiteData({ ...editingSiteData, code: e.target.value.toUpperCase() })}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <select
                                  value={editingSiteData.timezone}
                                  onChange={(e) => setEditingSiteData({ ...editingSiteData, timezone: e.target.value })}
                                  className="inline-select"
                                >
                                  <option value="America/New_York">Eastern (ET)</option>
                                  <option value="America/Chicago">Central (CT)</option>
                                  <option value="America/Denver">Mountain (MT)</option>
                                  <option value="America/Phoenix">Arizona (MST)</option>
                                  <option value="America/Los_Angeles">Pacific (PT)</option>
                                  <option value="America/Anchorage">Alaska (AKT)</option>
                                  <option value="Pacific/Honolulu">Hawaii (HST)</option>
                                </select>
                              </td>
                              <td>
                                <span className={`status-badge ${site.is_active ? 'active' : 'inactive'}`}>
                                  {site.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <div className="room-actions">
                                  <button onClick={saveSiteEdit} className="icon-button" title="Save">
                                    <Save size={16} />
                                  </button>
                                  <button onClick={cancelSiteEdit} className="icon-button" title="Cancel">
                                    <X size={16} />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{site.name}</td>
                              <td><code>{site.code}</code></td>
                              <td>
                                <select
                                  value={site.timezone || 'America/New_York'}
                                  onChange={(e) => updateSite(site.id, { timezone: e.target.value })}
                                  className="inline-select"
                                >
                                  <option value="America/New_York">Eastern (ET)</option>
                                  <option value="America/Chicago">Central (CT)</option>
                                  <option value="America/Denver">Mountain (MT)</option>
                                  <option value="America/Phoenix">Arizona (MST)</option>
                                  <option value="America/Los_Angeles">Pacific (PT)</option>
                                  <option value="America/Anchorage">Alaska (AKT)</option>
                                  <option value="Pacific/Honolulu">Hawaii (HST)</option>
                                </select>
                              </td>
                              <td>
                                <span className={`status-badge ${site.is_active ? 'active' : 'inactive'}`}>
                                  {site.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <div className="room-actions">
                                  <button onClick={() => startEditingSite(site)} className="icon-button" title="Edit">
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => navigateToSite(site)}
                                    className="icon-button"
                                    title="View Users"
                                    style={{ color: '#1a73e8' }}
                                  >
                                    <Users size={16} />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {getNetworkSites().length === 0 && (
                    <p className="empty-message">No sites in this network yet.</p>
                  )}
                </div>
              </>
            )}

            {/* Users Level */}
            {globalNavLevel === 'users' && selectedSiteForGlobal && (
              <>
                <p className="settings-description" style={{ marginBottom: '1rem' }}>
                  Users with access to {selectedSiteForGlobal.name}
                </p>

                <div className="sites-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Access Type</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {siteUsers.map(u => (
                        <tr key={u.id}>
                          <td>{u.full_name}</td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`role-badge ${u.role}`}>
                              {u.role === 'super_admin' ? 'Super Admin' : u.role === 'administrator' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${u.access_type === 'network' ? 'active' : 'info'}`}>
                              {u.access_type === 'network' ? 'Network' : 'Site'}
                            </span>
                          </td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {siteUsers.length === 0 && (
                    <p className="empty-message">No users have access to this site yet.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Domains Tab (Super Admin) */}
        {activeTab === 'domains' && isSuperAdmin && (
          <div className="tab-content">
            <h3>Email Domains</h3>
            <p className="settings-description">
              Configure which email domains are allowed to sign up for each site.
            </p>

            <div className="site-selector" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Select Site:
              </label>
              <select
                value={selectedSiteForDomains || currentSite?.id || ''}
                onChange={(e) => setSelectedSiteForDomains(e.target.value)}
                style={{ width: '100%', maxWidth: '400px', padding: '0.5rem' }}
              >
                {sites.map((site: any) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.networks?.name || 'No network'})
                  </option>
                ))}
              </select>
            </div>

            <div className="add-room">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="domain.com"
                onKeyPress={(e) => e.key === 'Enter' && addDomain()}
              />
              <button onClick={addDomain} className="add-button">
                <Plus size={16} />
                Add Domain
              </button>
            </div>

            <div className="rooms-list">
              {siteDomains.map(domain => (
                <div key={domain.id} className="room-item">
                  <span className="room-name">
                    @{domain.domain}
                    {domain.is_primary && <span className="primary-badge">Primary</span>}
                  </span>
                  <div className="room-actions">
                    <button
                      onClick={() => deleteDomain(domain.id)}
                      className="icon-button danger"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {siteDomains.length === 0 && (
                <p className="empty-message">No email domains configured. Users won't be able to sign up for this site.</p>
              )}
            </div>
          </div>
        )}

        {/* Tool Management Tab (Super Admin) - Uses Entitlements System */}
        {activeTab === 'tool-management' && isSuperAdmin && (
          <div className="tab-content">
            <div className="entitlements-header">
              <div>
                <h3>Tool Management</h3>
                <p className="settings-description">
                  Control which tools are available to networks, sites, and users.
                  Grant or revoke access using entitlements. Changes cascade down the hierarchy.
                </p>
              </div>
              <button onClick={() => setShowTrialModal(true)} className="add-button">
                <Gift size={16} />
                Start Trial
              </button>
            </div>

            {/* Global Features Section */}
            <div className="management-section">
              <h4><Wrench size={16} /> Global Features</h4>
              <p className="settings-description">
                Toggle features globally. Inactive features are hidden from all users regardless of entitlements.
              </p>
              <div className="tools-config-list">
                {features.map(feature => (
                  <div key={feature.feature_key} className={`tool-config-item ${!feature.is_active ? 'inactive' : ''}`}>
                    <div className="tool-config-info">
                      <span className="tool-name">{feature.display_name}</span>
                      <span className="tool-route">{feature.route}</span>
                    </div>
                    <div className="tool-config-actions">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={feature.is_active}
                          onChange={(e) => toggleFeatureActive(feature.feature_key, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className={`toggle-label ${feature.is_active ? 'active' : 'inactive'}`}>
                        {feature.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
                {features.length === 0 && (
                  <p className="empty-message">No features configured.</p>
                )}
              </div>
            </div>

            {/* Network Access Section */}
            <div className="management-section">
              <h4><Globe size={16} /> Network Access</h4>
              <p className="settings-description">
                Grant tool access to entire networks. All sites in the network will inherit this access.
              </p>
              <div className="select-wrapper">
                <select
                  value={selectedNetworkForEntitlements}
                  onChange={(e) => {
                    setSelectedNetworkForEntitlements(e.target.value);
                    if (e.target.value) {
                      fetchEntitlements({ scope: 'network', network_id: e.target.value });
                    } else {
                      setEntitlements([]);
                    }
                  }}
                >
                  <option value="">Select a network...</option>
                  {networks.map(n => (
                    <option key={n.id} value={n.id}>{n.name}</option>
                  ))}
                </select>
              </div>
              {selectedNetworkForEntitlements && (
                <>
                  <div className="tools-config-list" style={{ marginTop: '1rem' }}>
                    {features.filter(f => f.is_active).map(feature => {
                      const ent = entitlements.find(e => e.feature_key === feature.feature_key && e.scope === 'network');
                      const isEnabled = !!ent;
                      return (
                        <div key={feature.feature_key} className={`tool-config-item ${!isEnabled ? 'inactive' : ''}`}>
                          <div className="tool-config-info">
                            <span className="tool-name">{feature.display_name}</span>
                            {ent && (
                              <span className={`source-badge ${getSourceBadgeClass(ent.source)}`}>
                                {ent.source}{ent.expires_at ? ` - ${formatExpirationDate(ent.expires_at)}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="tool-config-actions">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    grantEntitlement('network', selectedNetworkForEntitlements, feature.feature_key);
                                  } else if (ent) {
                                    deleteEntitlement(ent.id);
                                  }
                                }}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                            <span className={`toggle-label ${isEnabled ? 'active' : 'inactive'}`}>
                              {isEnabled ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Site Access Section */}
            <div className="management-section">
              <h4><Building2 size={16} /> Site Access</h4>
              <p className="settings-description">
                Grant tool access to specific sites. Users at the site will have access to granted tools.
              </p>
              <div className="select-wrapper">
                <select
                  value={selectedSiteForEntitlements}
                  onChange={(e) => {
                    setSelectedSiteForEntitlements(e.target.value);
                    setSelectedUserForEntitlements('');
                    if (e.target.value) {
                      fetchEntitlements({ scope: 'site', site_id: e.target.value });
                    } else {
                      setEntitlements([]);
                    }
                  }}
                >
                  <option value="">Select a site...</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              {selectedSiteForEntitlements && (
                <>
                  <div className="tools-config-list" style={{ marginTop: '1rem' }}>
                    {features.filter(f => f.is_active).map(feature => {
                      const ent = entitlements.find(e => e.feature_key === feature.feature_key && e.scope === 'site');
                      const isEnabled = !!ent;
                      return (
                        <div key={feature.feature_key} className={`tool-config-item ${!isEnabled ? 'inactive' : ''}`}>
                          <div className="tool-config-info">
                            <span className="tool-name">{feature.display_name}</span>
                            {ent && (
                              <span className={`source-badge ${getSourceBadgeClass(ent.source)}`}>
                                {ent.source}{ent.expires_at ? ` - ${formatExpirationDate(ent.expires_at)}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="tool-config-actions">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    grantEntitlement('site', selectedSiteForEntitlements, feature.feature_key);
                                  } else if (ent) {
                                    deleteEntitlement(ent.id);
                                  }
                                }}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                            <span className={`toggle-label ${isEnabled ? 'active' : 'inactive'}`}>
                              {isEnabled ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* User Access Section */}
            <div className="management-section">
              <h4><Users size={16} /> User Access</h4>
              <p className="settings-description">
                Grant or revoke tool access for specific users at a site.
              </p>
              {!selectedSiteForEntitlements ? (
                <p className="empty-message">Select a site above to manage user tool access.</p>
              ) : (
                <>
                  <div className="select-wrapper">
                    <select
                      value={selectedUserForEntitlements}
                      onChange={(e) => {
                        setSelectedUserForEntitlements(e.target.value);
                        if (e.target.value) {
                          fetchEntitlements({ scope: 'user', user_id: e.target.value, site_id: selectedSiteForEntitlements });
                        }
                      }}
                    >
                      <option value="">Select a user...</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  {selectedUserForEntitlements && (
                    <div className="tools-config-list" style={{ marginTop: '1rem' }}>
                      {features.filter(f => f.is_active).map(feature => {
                        const ent = entitlements.find(e => e.feature_key === feature.feature_key && e.scope === 'user');
                        const isEnabled = !!ent;
                        return (
                          <div key={feature.feature_key} className={`tool-config-item ${!isEnabled ? 'inactive' : ''}`}>
                            <div className="tool-config-info">
                              <span className="tool-name">{feature.display_name}</span>
                              {ent && (
                                <span className={`source-badge ${getSourceBadgeClass(ent.source)}`}>
                                  {ent.source}{ent.expires_at ? ` - ${formatExpirationDate(ent.expires_at)}` : ''}
                                </span>
                              )}
                            </div>
                            <div className="tool-config-actions">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      grantEntitlement('user', selectedUserForEntitlements, feature.feature_key);
                                    } else if (ent) {
                                      deleteEntitlement(ent.id);
                                    }
                                  }}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                              <span className={`toggle-label ${isEnabled ? 'active' : 'inactive'}`}>
                                {isEnabled ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Quick Links Tab (Super Admin) */}
        {activeTab === 'quicklinks' && isSuperAdmin && (
          <div className="tab-content">
            <h3>Quick Links</h3>
            <p className="settings-description">
              Configure custom quick links that appear on the Tool Hub for each site.
              These can link to external resources, documentation, or commonly used tools.
            </p>

            {/* Site Selector */}
            <div className="management-section">
              <h4><Building2 size={16} /> Select Site</h4>
              <div className="select-wrapper">
                <select
                  value={selectedSiteForLinks}
                  onChange={(e) => setSelectedSiteForLinks(e.target.value)}
                >
                  <option value="">Select a site...</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedSiteForLinks && (
              <>
                {/* Add New Link */}
                <div className="management-section">
                  <h4><Plus size={16} /> Add New Link</h4>
                  <div className="add-link-form">
                    <input
                      type="text"
                      value={newLink.name}
                      onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                      placeholder="Link name..."
                    />
                    <input
                      type="url"
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                      placeholder="https://..."
                    />
                    <input
                      type="text"
                      value={newLink.description}
                      onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                      placeholder="Description (optional)..."
                    />
                    <button onClick={addSiteLink} className="add-button">
                      <Plus size={16} />
                      Add Link
                    </button>
                  </div>
                </div>

                {/* Existing Links */}
                <div className="management-section">
                  <h4><Link size={16} /> Site Links</h4>
                  {siteLinksLoading ? (
                    <p className="loading-message">Loading links...</p>
                  ) : siteLinks.length === 0 ? (
                    <p className="empty-message">No links configured for this site.</p>
                  ) : (
                    <div className="links-list">
                      {siteLinks.map(link => (
                        <div key={link.id} className={`link-item ${!link.is_active ? 'inactive' : ''}`}>
                          {editingLink === link.id ? (
                            <div className="link-edit-form">
                              <input
                                type="text"
                                value={editingLinkData.name}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, name: e.target.value })}
                                placeholder="Link name..."
                              />
                              <input
                                type="url"
                                value={editingLinkData.url}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, url: e.target.value })}
                                placeholder="https://..."
                              />
                              <input
                                type="text"
                                value={editingLinkData.description}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, description: e.target.value })}
                                placeholder="Description..."
                              />
                              <div className="link-edit-actions">
                                <button onClick={() => updateSiteLink(link.id)} className="btn-save">
                                  <Save size={14} />
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingLink(null);
                                    setEditingLinkData({ name: '', url: '', description: '' });
                                  }}
                                  className="btn-cancel"
                                >
                                  <X size={14} />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="link-info">
                                <span className="link-name">{link.name}</span>
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-url">
                                  <ExternalLink size={12} />
                                  {link.url}
                                </a>
                                {link.description && (
                                  <span className="link-description">{link.description}</span>
                                )}
                              </div>
                              <div className="link-actions">
                                <button
                                  onClick={() => {
                                    setEditingLink(link.id);
                                    setEditingLinkData({
                                      name: link.name,
                                      url: link.url,
                                      description: link.description || ''
                                    });
                                  }}
                                  className="icon-button"
                                  title="Edit link"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => toggleSiteLinkActive(link.id, !link.is_active)}
                                  className="icon-button"
                                  title={link.is_active ? 'Hide link' : 'Show link'}
                                >
                                  {link.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                                <button
                                  onClick={() => deleteSiteLink(link.id)}
                                  className="icon-button danger"
                                  title="Delete link"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Trial Modal */}
        {showTrialModal && (
          <div className="modal-overlay" onClick={() => setShowTrialModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Start a Trial</h2>
              <p className="settings-description">
                Grant temporary access to a feature for a site.
              </p>
              <div className="form-group">
                <label>Site</label>
                <select
                  value={trialForm.site_id}
                  onChange={(e) => setTrialForm({ ...trialForm, site_id: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">Select a site...</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Feature</label>
                <select
                  value={trialForm.feature_key}
                  onChange={(e) => setTrialForm({ ...trialForm, feature_key: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">Select a feature...</option>
                  {features.filter(f => f.is_active).map(f => (
                    <option key={f.feature_key} value={f.feature_key}>{f.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Duration (days)</label>
                <input
                  type="number"
                  value={trialForm.duration_days}
                  onChange={(e) => setTrialForm({ ...trialForm, duration_days: parseInt(e.target.value) || 30 })}
                  min="1"
                  max="365"
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={trialForm.notes}
                  onChange={(e) => setTrialForm({ ...trialForm, notes: e.target.value })}
                  placeholder="Reason for trial..."
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowTrialModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={startTrial} className="btn-primary">
                  <Gift size={16} />
                  Start Trial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAdmin;
